/**
 * Superadmin: overzicht programmatic SEO — live /kosten/pages.json + keyword-catalog.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileStack, RefreshCw } from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';

export function AdminSeoPages() {
  const [siteOrigin, setSiteOrigin] = useState('');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-seo-pages', siteOrigin || null],
    queryFn: () =>
      api.getAdminSeoPages({
        ...(siteOrigin.trim() ? { site_origin: siteOrigin.trim() } : {}),
      }),
  });

  const errMsg = error instanceof Error ? error.message : String(error ?? '');
  const isForbidden = errMsg.toLowerCase().includes('forbidden') || errMsg.toLowerCase().includes('superadmin');

  const pages = data?.pages && typeof data.pages === 'object' && data.pages !== null && 'pages' in data.pages
    ? (data.pages as { pages: Array<{ path: string; title: string; keyword?: string }> }).pages
    : [];

  const systems =
    data?.keyword_catalog &&
    typeof data.keyword_catalog === 'object' &&
    data.keyword_catalog !== null &&
    'systems' in data.keyword_catalog
      ? (data.keyword_catalog as { systems: unknown[] }).systems
      : [];

  return (
    <div>
      <SeoMeta
        path="/admin/seo-pages"
        noindex
        title="SEO pagina’s (kosten)"
        description="Superadmin: lijst van gegenereerde /kosten SEO-pagina’s en catalogus-snapshot."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'SEO pagina’s' }]} />
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <FileStack className="h-6 w-6 text-primary" aria-hidden />
          <h1 className="text-2xl font-bold text-text">SEO pagina’s (/kosten)</h1>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="seo-pages-origin" className="mb-1 block text-xs text-text-muted">
              Site-origin (optioneel)
            </label>
            <input
              id="seo-pages-origin"
              type="url"
              placeholder="https://www.calqtool.com"
              value={siteOrigin}
              onChange={(e) => setSiteOrigin(e.target.value)}
              className="w-full min-w-[14rem] rounded-lg border border-border bg-surface-card px-3 py-2 text-sm text-text lg:w-64"
            />
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text hover:bg-surface disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Vernieuwen
          </button>
        </div>
      </div>

      {isForbidden && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          Alleen Platformbeheer (rol superadmin) heeft toegang tot dit overzicht.
        </p>
      )}

      {error && !isForbidden && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
          {errMsg}
        </p>
      )}

      {isLoading && <p className="text-sm text-text-muted">Laden…</p>}

      {data && (
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-surface-card p-4 text-sm text-text">
            <p className="font-medium text-text">Bron</p>
            <p className="mt-1 text-text-muted">
              <span className="text-text">{data.site_origin}</span> ({data.site_origin_source}) ·{' '}
              {data.page_count} URL’s in <code className="rounded bg-surface px-1">pages.json</code>
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              <a
                href={data.pages_json_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                pages.json <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <a
                href={data.keyword_catalog_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                keyword-catalog.json <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
            {!data.pages_fetch_ok && (
              <p className="mt-2 text-amber-800 dark:text-amber-200">
                pages.json niet bereikbaar (HTTP {data.pages_http_status}). Controleer deploy en of{' '}
                <code className="rounded bg-surface px-1">pnpm generate:seo</code> is gedraaid.
              </p>
            )}
            {!data.keyword_catalog_fetch_ok && (
              <p className="mt-2 text-amber-800 dark:text-amber-200">
                keyword-catalog.json niet bereikbaar (HTTP {data.keyword_catalog_http_status}). Opnieuw SEO-build
                draaien.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-surface-card p-4">
            <h2 className="mb-3 text-lg font-semibold text-text">Gepubliceerde SEO-detailpagina’s</h2>
            <div className="max-h-[min(28rem,50vh)] overflow-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-surface text-text-muted">
                  <tr>
                    <th className="border-b border-border px-3 py-2 font-medium">Pad</th>
                    <th className="border-b border-border px-3 py-2 font-medium">Titel</th>
                    <th className="border-b border-border px-3 py-2 font-medium">Keyword</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((row) => (
                    <tr key={row.path} className="border-b border-border/80 hover:bg-surface/80">
                      <td className="px-3 py-2 font-mono text-xs">
                        <a
                          href={`${data.site_origin.replace(/\/$/, '')}${row.path}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                        >
                          {row.path}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-text">{row.title}</td>
                      <td className="px-3 py-2 text-text-muted">{row.keyword ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-surface-card p-4">
            <h2 className="mb-3 text-lg font-semibold text-text">Catalogus (alle systemen)</h2>
            <p className="mb-2 text-sm text-text-muted">
              {systems.length} systemen in seed — ter referentie voor uitbreiding van het SEO-raster.
            </p>
            <div className="max-h-[min(20rem,40vh)] overflow-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-surface text-text-muted">
                  <tr>
                    <th className="border-b border-border px-3 py-2 font-medium">system_id</th>
                    <th className="border-b border-border px-3 py-2 font-medium">Label</th>
                    <th className="border-b border-border px-3 py-2 font-medium">Discipline</th>
                    <th className="border-b border-border px-3 py-2 font-medium">Varianten</th>
                  </tr>
                </thead>
                <tbody>
                  {(systems as Array<{ system_id: string; label: string; discipline: string; variant_ids?: string[] }>).map(
                    (s) => (
                      <tr key={s.system_id} className="border-b border-border/80 hover:bg-surface/80">
                        <td className="px-3 py-2 font-mono text-xs">{s.system_id}</td>
                        <td className="px-3 py-2 text-text">{s.label}</td>
                        <td className="px-3 py-2 text-text-muted">{s.discipline}</td>
                        <td className="px-3 py-2 text-text-muted">{s.variant_ids?.length ?? '—'}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
